import { Injectable, signal } from '@angular/core';

const OPEN_KEY = 'jms:inline-sidenav:open-groups';
const FORCE_CLOSED_KEY = 'jms:inline-sidenav:force-closed-groups';

@Injectable({ providedIn: 'root' })
export class SidenavPersistenceService {

    public readonly $openGroups = signal<Set<string>>(this.read(OPEN_KEY));
    public readonly $forceClosedGroups = signal<Set<string>>(this.read(FORCE_CLOSED_KEY));

    public toggle(path: string, hasActiveChild: boolean): void {

        const open = this.$openGroups();
        const forceClosed = this.$forceClosedGroups();
        const isCurrentlyOpen = open.has(path) || (hasActiveChild && !forceClosed.has(path));

        if (isCurrentlyOpen) {
            const nextOpen = new Set(open);
            nextOpen.delete(path);
            this.$openGroups.set(nextOpen);
            this.write(OPEN_KEY, nextOpen);

            if (hasActiveChild) {
                const nextClosed = new Set(forceClosed);
                nextClosed.add(path);
                this.$forceClosedGroups.set(nextClosed);
                this.write(FORCE_CLOSED_KEY, nextClosed);
            }
            return;
        }

        const nextOpen = new Set(open);
        nextOpen.add(path);
        this.$openGroups.set(nextOpen);
        this.write(OPEN_KEY, nextOpen);

        const nextClosed = new Set(forceClosed);
        nextClosed.delete(path);
        this.$forceClosedGroups.set(nextClosed);
        this.write(FORCE_CLOSED_KEY, nextClosed);
    }

    public clearForceClosed(path: string): void {

        const current = this.$forceClosedGroups();
        if (!current.has(path)) return;
        const next = new Set(current);
        next.delete(path);
        this.$forceClosedGroups.set(next);
        this.write(FORCE_CLOSED_KEY, next);
    }

    public isOpen(path: string): boolean {

        return this.$openGroups().has(path);
    }

    public isForceClosed(path: string): boolean {

        return this.$forceClosedGroups().has(path);
    }

    private read(key: string): Set<string> {

        try {
            const raw = localStorage.getItem(key);
            if (!raw) return new Set();
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? new Set(parsed) : new Set();
        } catch {
            return new Set();
        }
    }

    private write(key: string, set: Set<string>): void {

        try {
            localStorage.setItem(key, JSON.stringify(Array.from(set)));
        } catch {
            return;
        }
    }
}
